/**
 * Seeded demo org events stay in the database after their customer flag is
 * turned off. These helpers hide each one unless its own flag is on.
 */
import { useMemo } from 'react';
import {
  CFA_HELENE_ORG_EVENT_DEMO_KEY,
  CNIC_HAWAII_ORG_EVENT_DEMO_KEY,
  DHS_REGION4_ORG_EVENT_DEMO_KEY,
  EPA_GULF_ORG_EVENT_DEMO_KEY,
  EQUINOR_GRANE_ORG_EVENT_DEMO_KEY,
  JAPAN_CHEMICAL_ORG_EVENT_DEMO_KEY,
  JAPAN_ORG_EVENT_DEMO_KEY,
  ONTARIO_GTA_ORG_EVENT_DEMO_KEY,
  TNNG_MEMPHIS_ORG_EVENT_DEMO_KEY,
  USCG_FOC_SE_ORG_EVENT_DEMO_KEY,
} from 'hooks/orgEvents/orgEventHooks';
import {
  useCfaFlag,
  useCnicFlag,
  useDhsFlag,
  useEpaFlag,
  useEquinorFlag,
  useGovernmentOfOntarioFlag,
  useJapanDemoFlag,
  useTennesseeNationalGuardFlag,
  useUscgFocRequirementsFlag,
} from 'utils/featureFlags';

/** Every demo key that is gated by a feature flag. */
export const FLAG_GATED_DEMO_EVENT_KEYS: readonly string[] = [
  CFA_HELENE_ORG_EVENT_DEMO_KEY,
  EPA_GULF_ORG_EVENT_DEMO_KEY,
  DHS_REGION4_ORG_EVENT_DEMO_KEY,
  EQUINOR_GRANE_ORG_EVENT_DEMO_KEY,
  JAPAN_ORG_EVENT_DEMO_KEY,
  JAPAN_CHEMICAL_ORG_EVENT_DEMO_KEY,
  ONTARIO_GTA_ORG_EVENT_DEMO_KEY,
  CNIC_HAWAII_ORG_EVENT_DEMO_KEY,
  TNNG_MEMPHIS_ORG_EVENT_DEMO_KEY,
  USCG_FOC_SE_ORG_EVENT_DEMO_KEY,
];

const GATED = new Set(FLAG_GATED_DEMO_EVENT_KEYS);

/**
 * Drops known demo events whose key is not enabled. User-created events and
 * unknown demo keys always pass.
 */
export function filterFlagGatedDemoEvents<
  T extends { demo_key?: string | null },
>(events: T[], enabledDemoKeys: ReadonlySet<string>): T[] {
  return events.filter(
    (event) =>
      !event.demo_key ||
      !GATED.has(event.demo_key) ||
      enabledDemoKeys.has(event.demo_key)
  );
}

/** Demo event keys whose owning feature flag is on for the current org. */
export function useEnabledDemoEventKeys(): ReadonlySet<string> {
  const cfa = useCfaFlag();
  const epa = useEpaFlag();
  const dhs = useDhsFlag();
  const equinor = useEquinorFlag();
  const japan = useJapanDemoFlag();
  const ontario = useGovernmentOfOntarioFlag();
  const cnic = useCnicFlag();
  const tnng = useTennesseeNationalGuardFlag();
  const uscgFoc = useUscgFocRequirementsFlag();

  return useMemo(() => {
    const enabled: Array<[boolean, string[]]> = [
      [cfa, [CFA_HELENE_ORG_EVENT_DEMO_KEY]],
      [epa, [EPA_GULF_ORG_EVENT_DEMO_KEY]],
      [dhs, [DHS_REGION4_ORG_EVENT_DEMO_KEY]],
      [equinor, [EQUINOR_GRANE_ORG_EVENT_DEMO_KEY]],
      [japan, [JAPAN_ORG_EVENT_DEMO_KEY, JAPAN_CHEMICAL_ORG_EVENT_DEMO_KEY]],
      [ontario, [ONTARIO_GTA_ORG_EVENT_DEMO_KEY]],
      [cnic, [CNIC_HAWAII_ORG_EVENT_DEMO_KEY]],
      [tnng, [TNNG_MEMPHIS_ORG_EVENT_DEMO_KEY]],
      [uscgFoc, [USCG_FOC_SE_ORG_EVENT_DEMO_KEY]],
    ];
    return new Set(enabled.flatMap(([on, keys]) => (on ? keys : [])));
  }, [cfa, epa, dhs, equinor, japan, ontario, cnic, tnng, uscgFoc]);
}
